import EVImg from '@components/EVImg/EVImg';
import Block from '@components/Layout/Block';
import {
  CarouselNavButtonLeft,
  CarouselNavButtonRight,
} from '@components/MiscComponents/GridAndCarouselComponents/index';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import RichText from '@components/RichText';
import { Document } from '@contentful/rich-text-types';
import Link from 'next/link';
import { A11y, Navigation } from 'swiper/modules';
import { Swiper } from 'swiper/react';

import {
  Body,
  ButtonContainer,
  Heading,
  SliderContainer,
  StyledSwiperSlide,
  TopContainer,
} from './styles';
import { ReactNode } from 'react';

import { useTranslations } from 'next-intl';

export interface LifestylesProps {
  lifestyleCarousel: any;
  lifestyleBody: string;
  lifestyleHeader: string;
}

// PARENT COMPONENT
const Lifestyles = ({
  lifestyleCarousel,
  lifestyleBody,
  lifestyleHeader,
}: LifestylesProps): ReactNode => {
  const t = useTranslations();
  const lifestyles = lifestyleCarousel?.fields?.lifestyles?.map((lifestyle) => {
    const cardHeader = lifestyle?.fields?.header ? lifestyle?.fields?.header : null;

    //convert lifestyleHeader to lowercase and replace spaces with dashes
    const lifestyleSlug =
      lifestyle?.fields?.type && lifestyle?.fields?.type.length
        ? lifestyle?.fields?.type[0].toLowerCase().replace(/\s+/g, '-')
        : null;
    return {
      key: lifestyle?.sys?.id,
      header: cardHeader,
      img: lifestyle?.fields?.img?.fields?.file?.url,
      url: `https://www.evrealestate.com/lifestyle-properties/${lifestyleSlug}`,
    };
  });
  if (lifestyleCarousel?.fields?.lifestyles && lifestyleCarousel?.fields?.lifestyles.length > 0) {
    return (
      <Block>
        <TopContainer>
          <Heading>{lifestyleHeader ?? t('followYourDreamHeader')}</Heading>

          <VerticalLineSeparator />

          <Body>
            {lifestyleBody &&
              (typeof lifestyleBody === 'string' ? (
                <p>{lifestyleBody as string}</p>
              ) : (
                <RichText richText={lifestyleBody as Document} />
              ))}
          </Body>
        </TopContainer>
        {lifestyles && (
          <SliderContainer>
            <Swiper
              modules={[Navigation, A11y]}
              slidesPerView={2}
              spaceBetween={16}
              centerInsufficientSlides={true}
              data-testid="lifestyles-swiper"
              navigation={{
                prevEl: '.lifestyle-prev',
                nextEl: '.lifestyle-next',
              }}
              a11y={{
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
              }}
              breakpoints={{
                535: {
                  slidesPerView: 3,
                },
                900: {
                  slidesPerView: 4,
                },
                1156: {
                  slidesPerView: 5,
                },
                1330: {
                  slidesPerView: 6,
                },
              }}
            >
              {lifestyles.map((s) => {
                //ShopGroup and Shop pages will link to the lifestyles of the America's shop
                const isExternal = s.url && s.url.includes('www.evrealestate.com');

                return (
                  <StyledSwiperSlide key={s.key}>
                    {s.url ? (
                      <Link href={s.url} target={isExternal ? '_blank' : '_self'}>
                        <EVImg src={s.img} sizes={'200px'} priority={true} />
                        <span>{s.header}</span>
                      </Link>
                    ) : (
                      <>
                        <EVImg src={s.img} sizes={'200px'} priority={true} />
                        <span>{s.header}</span>
                      </>
                    )}
                  </StyledSwiperSlide>
                );
              })}
              <ButtonContainer>
                <CarouselNavButtonLeft className="lifestyle-prev" />
                <CarouselNavButtonRight className="lifestyle-next" />
              </ButtonContainer>
            </Swiper>
          </SliderContainer>
        )}
      </Block>
    );
  }
};

export default Lifestyles;
